import React, { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  Gauge, TrendingUp, Loader2, Lock, ArrowRight,
  Sparkles, CheckCircle2, AlertCircle, ShieldCheck,
  Facebook, Instagram, Linkedin, Youtube, Twitter, Music,
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const CERT_FOR = (score) =>
  score >= 85 ? { grade: "Traction Leader", color: "#c9a84c", bg: "bg-gold/15", border: "border-gold" } :
  score >= 70 ? { grade: "Growth Ready",    color: "#5bbf85", bg: "bg-green-500/15", border: "border-green-500/40" } :
  score >= 55 ? { grade: "Developing",      color: "#c9a84c", bg: "bg-yellow-500/15", border: "border-yellow-500/40" } :
  score >= 40 ? { grade: "At Risk",         color: "#cd7f32", bg: "bg-orange-500/15", border: "border-orange-500/40" } :
                { grade: "Critical",        color: "#e85b5b", bg: "bg-red-500/15", border: "border-red-500/40" };

const Sev = ({ s }) => (
  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
    s === "High" ? "bg-[color:var(--auditiq-critical)]/20 text-[color:var(--auditiq-critical)]" :
    s === "Medium" ? "bg-gold/20 text-gold" :
    "bg-steel/20 text-cream-dim"
  }`}>{s}</span>
);

const SOCIAL_FIELDS = [
  { key: "facebook",  label: "Facebook",  Icon: Facebook, placeholder: "https://facebook.com/yourpage" },
  { key: "instagram", label: "Instagram", Icon: Instagram, placeholder: "https://instagram.com/yourhandle" },
  { key: "linkedin",  label: "LinkedIn",  Icon: Linkedin, placeholder: "https://linkedin.com/company/yourco" },
  { key: "youtube",   label: "YouTube",   Icon: Youtube,  placeholder: "https://youtube.com/@yourchannel" },
  { key: "twitter",   label: "X / Twitter", Icon: Twitter, placeholder: "https://x.com/yourhandle" },
  { key: "tiktok",    label: "TikTok",    Icon: Music,    placeholder: "https://tiktok.com/@yourhandle" },
];

const FreeMarketingAudit = () => {
  // Lead capture
  const [lead, setLead] = useState({ name: "", email: "", organization: "", website: "", phone: "" });
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [leadBusy, setLeadBusy] = useState(false);

  // Scan inputs (populated from lead)
  const [url, setUrl] = useState("");
  const [org, setOrg] = useState("");
  const [socials, setSocials] = useState({
    facebook: "", instagram: "", linkedin: "", youtube: "", twitter: "", tiktok: "",
  });
  const [emailGate, setEmailGate] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const setL = (k) => (e) => setLead({ ...lead, [k]: e.target.value });
  const setS = (k) => (e) => setSocials({ ...socials, [k]: e.target.value });

  const captureLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim()) return toast.error("Name is required");
    if (!lead.email.trim() || !lead.email.includes("@")) return toast.error("Valid email is required");
    if (!lead.organization.trim()) return toast.error("Organization is required");
    if (!lead.website.trim()) return toast.error("Website is required");
    if (!/^https?:\/\//i.test(lead.website)) return toast.error("Website must start with http:// or https://");

    setLeadBusy(true);
    try {
      await axios.post(`${API}/leads/traction-capture`, {
        name: lead.name,
        email: lead.email,
        organization: lead.organization,
        website: lead.website,
        phone: lead.phone,
      }, { timeout: 15000 });
      // Pre-fill scan inputs
      setUrl(lead.website);
      setOrg(lead.organization);
      setEmailGate(lead.email);
      setLeadCaptured(true);
      toast.success("Thanks! Let's scan your site.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your info");
    }
    setLeadBusy(false);
  };

  const runScan = async (includeEmail = false) => {
    if (!url) return toast.error("Enter your website URL");
    if (!/^https?:\/\//i.test(url))
      return toast.error("URL must start with http:// or https://");
    setBusy(true);
    try {
      const socialList = SOCIAL_FIELDS
        .map(f => socials[f.key]?.trim())
        .filter(Boolean);
      const r = await axios.post(`${API}/auditiq/public/free-marketing-audit`, {
        website_url: url,
        organization_name: org,
        email: includeEmail ? emailGate : lead.email || "",
        social_urls: socialList,
      }, { timeout: 45000 });
      setResult(r.data);
      if (includeEmail) toast.success("Full report unlocked — sent to your inbox.");
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429) toast.error("You've used all 5 free scans this hour — sign up for unlimited.");
      else toast.error(err?.response?.data?.detail || "Scan failed");
    }
    setBusy(false);
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (!emailGate || !emailGate.includes("@")) return toast.error("Enter a valid email");
    await runScan(true);
  };

  const cert = result ? CERT_FOR(result.traction?.traction_score || 0) : null;

  return (
    <Layout>
      <SEOHead title="Free Marketing Audit | AuditIQ" path="/free-audit"
               description="Get an instant Traction Score, SEO health snapshot, and top revenue opportunities for your website — free." />

      {/* HERO */}
      <section className="border-b border-gold-dim/20">
        <div className="container-wide py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold mb-4">
              <Sparkles size={12} /> Free · 30-Second Audit
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-cream leading-tight">
              Get your <span className="text-gold">Traction Score</span> in 30 seconds.
            </h1>
            <p className="text-cream-dim text-lg mt-5 leading-relaxed max-w-2xl">
              Instant AI-powered snapshot of your website's SEO, conversion funnel, social
              presence, and reputation. We'll analyze your website and social profiles and
              email you the full report.
            </p>
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE (pre-scan) */}
      {!leadCaptured && (
        <section className="container-wide py-10">
          <form onSubmit={captureLead} className="glass p-6 max-w-4xl" data-testid="traction-lead-form">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold mb-4">
              <ShieldCheck size={12} /> Tell us who you are to get started
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Full Name *</label>
                <Input value={lead.name} onChange={setL("name")} required placeholder="Jane Doe"
                  className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-11"
                  data-testid="lead-name-input" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Work Email *</label>
                <Input type="email" value={lead.email} onChange={setL("email")} required
                  placeholder="jane@yourcompany.com"
                  className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-11"
                  data-testid="lead-email-input" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Organization *</label>
                <Input value={lead.organization} onChange={setL("organization")} required
                  placeholder="Acme Corp"
                  className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-11"
                  data-testid="lead-org-input" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Website URL *</label>
                <Input value={lead.website} onChange={setL("website")} required
                  placeholder="https://yourcompany.com"
                  className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-11"
                  data-testid="lead-website-input" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Phone (optional)</label>
                <Input value={lead.phone} onChange={setL("phone")}
                  placeholder="+1 555 123 4567"
                  className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-11"
                  data-testid="lead-phone-input" />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
              <div className="text-[11px] text-cream-dim max-w-md">
                We use this to email your full report. No spam — one-time delivery unless you opt in.
              </div>
              <Button type="submit" disabled={leadBusy} className="btn-gold h-12 px-6"
                      data-testid="lead-continue-btn">
                {leadBusy ? <><Loader2 size={14} className="animate-spin mr-2" />Saving…</>
                          : <>Continue to Scan <ArrowRight size={14} className="ml-2" /></>}
              </Button>
            </div>
          </form>
        </section>
      )}

      {/* SCAN FORM (after lead capture) */}
      {leadCaptured && (
        <section className="container-wide py-10">
          <div className="glass p-6 max-w-4xl" data-testid="free-audit-form">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-electric mb-4">
              <CheckCircle2 size={12} /> Thanks {lead.name.split(" ")[0]} — now add any social profiles you want us to analyze.
            </div>

            <div className="grid md:grid-cols-12 gap-4">
              <div className="md:col-span-7">
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Your website URL *</label>
                <Input
                  value={url} onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://yourcompany.com"
                  className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-12"
                  data-testid="free-audit-url-input"
                />
              </div>
              <div className="md:col-span-5">
                <label className="text-[11px] uppercase tracking-[0.22em] text-steel">Company name</label>
                <Input
                  value={org} onChange={(e) => setOrg(e.target.value)}
                  placeholder="Acme Corp"
                  className="mt-1 bg-navy-deep border-gold-dim/40 text-cream h-12"
                  data-testid="free-audit-org-input"
                />
              </div>
            </div>

            {/* Social profiles */}
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-2">
                Social Profiles (optional — paste any URLs you want analyzed)
              </div>
              <div className="grid md:grid-cols-2 gap-3" data-testid="social-urls-grid">
                {SOCIAL_FIELDS.map(({ key, label, Icon, placeholder }) => (
                  <div key={key} className="flex items-center gap-2">
                    <div className="w-9 h-11 flex items-center justify-center bg-navy-deep border border-gold-dim/40 rounded-l text-gold">
                      <Icon size={14} />
                    </div>
                    <Input
                      value={socials[key]}
                      onChange={setS(key)}
                      placeholder={placeholder}
                      className="flex-1 bg-navy-deep border-gold-dim/40 text-cream h-11 rounded-l-none"
                      data-testid={`social-url-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
              <div className="text-[11px] text-cream-dim">
                5 free scans per hour · We only save what you submit.
              </div>
              <Button
                onClick={() => runScan(false)} disabled={busy}
                className="btn-gold h-12 px-6"
                data-testid="free-audit-run-btn"
              >
                {busy ? <><Loader2 size={14} className="animate-spin mr-2" />Scanning…</>
                      : <><TrendingUp size={14} className="mr-2" />Run Free Audit</>}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* RESULT */}
      {result && cert && (
        <section className="container-wide pb-16 space-y-6" data-testid="free-audit-result">
          <div className={`glass p-8 border ${cert.border} ${cert.bg}`} data-testid="free-audit-score">
            <div className="flex items-start justify-between flex-wrap gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.26em] text-electric">Your Traction Score</div>
                <div className="flex items-baseline gap-3 mt-2">
                  <div className="metric text-7xl" style={{ color: cert.color }}>
                    {result.traction?.traction_score}
                  </div>
                  <div className="text-2xl text-cream-dim">/100</div>
                </div>
                <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-bold"
                     style={{ background: cert.color + "22", color: cert.color, border: `1px solid ${cert.color}55` }}>
                  <Gauge size={12} className="mr-1" /> {result.traction?.grade}
                </div>
              </div>
              <div className="space-y-2 text-right">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-cream-dim">Total Issues Found</div>
                  <div className="metric text-3xl text-gold">{result.traction?.total_findings}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-cream-dim">High Severity</div>
                  <div className="metric text-3xl text-[color:var(--auditiq-critical)]">{result.traction?.high_count}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Top findings */}
          <div className="glass p-6" data-testid="free-audit-findings">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.26em] text-electric">
                  {result.gated ? "Top 3 Issues" : "All Findings"}
                </div>
                <h3 className="font-display text-xl text-cream mt-1">
                  Here's what your site is telling search engines & visitors
                </h3>
              </div>
              {result.gated && (
                <div className="text-[11px] text-gold inline-flex items-center gap-1">
                  <Lock size={12} /> {result.hidden_count} more hidden
                </div>
              )}
            </div>

            <div className="mt-5 space-y-3">
              {(result.findings || []).map((f, i) => (
                <div key={i} className="p-4 rounded bg-navy-deep border border-gold-dim/20"
                     data-testid={`free-audit-finding-${i}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-2">
                      {f.severity === "High"
                        ? <AlertCircle size={16} className="text-[color:var(--auditiq-critical)] mt-0.5" />
                        : <CheckCircle2 size={16} className="text-gold mt-0.5" />}
                      <div>
                        <div className="font-medium text-cream">{f.title}</div>
                        <div className="text-xs text-cream-dim mt-1">{f.category} · {f.department}</div>
                      </div>
                    </div>
                    <Sev s={f.severity} />
                  </div>
                  <div className="mt-3 text-xs text-cream-dim">{f.recommendation}</div>
                </div>
              ))}
            </div>

            {result.gated && (
              <div className="mt-6 p-6 rounded border-2 border-gold bg-gold/10" data-testid="free-audit-email-gate">
                <div className="flex items-start gap-3">
                  <Lock size={20} className="text-gold mt-1" />
                  <div className="flex-1">
                    <div className="font-display text-xl text-cream">
                      See all {result.traction?.total_findings} findings — send the full report
                    </div>
                    <p className="text-sm text-cream-dim mt-1">
                      Confirm your email to unlock the full audit instantly and send a PDF copy to your inbox.
                    </p>
                    <form onSubmit={unlock} className="mt-4 flex flex-wrap gap-2">
                      <Input
                        type="email" value={emailGate} onChange={(e) => setEmailGate(e.target.value)}
                        placeholder="you@yourcompany.com"
                        className="flex-1 min-w-[240px] bg-navy-deep border-gold-dim/40 text-cream h-11"
                        data-testid="free-audit-email-input"
                      />
                      <Button type="submit" disabled={busy} className="btn-gold h-11 px-5"
                              data-testid="free-audit-unlock-btn">
                        {busy ? <><Loader2 size={14} className="animate-spin mr-2" />Unlocking…</>
                              : <>Unlock Full Report <ArrowRight size={14} className="ml-2" /></>}
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="glass p-6 border-l-4 border-gold" data-testid="free-audit-revenue">
            <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Estimated Annual Revenue Opportunity</div>
            <div className="font-display text-4xl text-gold">
              ${(result.traction?.annual_revenue_opportunity || 0).toLocaleString()}
            </div>
            <p className="text-[11px] text-cream-dim mt-3 leading-relaxed max-w-3xl">
              {result.disclaimer}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/portal/login">
                <Button className="btn-gold h-11 px-5" data-testid="free-audit-signup-btn">
                  Get unlimited audits <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
              <Link to="/portal/demo/marketing_traction_gap">
                <Button className="btn-ghost-gold h-11 px-5" data-testid="free-audit-demo-btn">
                  See a sample full report
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default FreeMarketingAudit;
